import React, { useEffect, useState, Children, Fragment, memo } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { ChevronAccent } from '../components/ChevronAccent';
import { TopographicPattern } from '../components/TopographicPattern';
export function WhatsWrong() {
  const [expandedTax, setExpandedTax] = useState<number | null>(null);
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  // Hero-specific sequenced animations — deliberate pacing
  const heroEyebrow = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 0.1,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const heroHeadline = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.9,
        delay: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  // The pause lives here — headline finishes ~1.25s, body starts at 1.5s
  const heroBody = {
    hidden: {
      opacity: 0,
      y: 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 1.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const heroCta = {
    hidden: {
      opacity: 0,
      y: 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 1.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const heroMicrocopy = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.4,
        delay: 2.0
      }
    }
  };
  const heroNav = {
    hidden: {
      opacity: 0,
      y: 10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 2.15,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const taxes = [{
    num: '1',
    anchor: 'dependence',
    name: 'Dependence Tax',
    oneLine: 'Too much still depends on the founder or a few key people.',
    feelsLike: 'If the right person is out, progress slows or stops.',
    signals: ['work slows or stalls if a key person is out', 'decisions and execution depend on who is involved', 'the same questions repeat because the logic is not accessible', 'people hesitate because they do not trust their understanding', 'important context lives in Slack threads, DMs, and memory'],
    commonMisread: 'It looks like a people problem. It is often trapped logic, missing decision rules, or knowledge that never got externalized.',
    validate: ['Where decisions still rely on memory, interpretation, or one person carrying too much context']
  }, {
    num: '2',
    anchor: 'opportunity',
    name: 'Opportunity Tax',
    oneLine: 'Leadership has no room for the future because today keeps eating all the oxygen.',
    feelsLike: 'Strategic work keeps getting pushed because the business cannot run without constant intervention.',
    signals: ['strategic work keeps getting postponed', 'leaders become a pull to review, prevent, and backstop', 'the future keeps losing to today', 'leaders are needed to keep execution moving', 'the business cannot protect time to build what is next'],
    commonMisread: 'It looks like weak planning or bad time management. It is often a load-path problem.',
    validate: ['What keeps routing important work back into the urgent pile']
  }, {
    num: '3',
    anchor: 'leakage',
    name: 'Leakage Tax',
    oneLine: 'Margin leaks through exceptions, rework, unclear scope, and work that should have resolved earlier.',
    feelsLike: 'Revenue is coming in, but profitability does not follow the way it should.',
    signals: ['just this once happens constantly', 'what is sold drifts from what fulfillment can reliably sustain', 'margin erodes through exceptions, rework, and unpriced complexity', 'teams spend energy on cleanup instead of improvement', 'reliability depends on heroics'],
    commonMisread: 'It looks like pricing. Sometimes it is. Often it is unmanaged variance.',
    validate: ['Where exceptions enter, how they become normal, and where profit is quietly being lost']
  }, {
    num: '4',
    anchor: 'visibility',
    name: 'Visibility Tax',
    oneLine: 'The numbers create debate instead of decisions.',
    feelsLike: 'Leaders cannot tell what is true fast enough to move with confidence.',
    signals: ['different systems report different answers to the same question', 'meetings start by reconciling data instead of deciding', 'leaders do not trust the signal, so they default to opinion', 'what is true takes too long to answer', 'action waits for someone to validate reality'],
    commonMisread: 'It looks like a dashboard problem. It is often a definitions problem.',
    validate: ['Which metrics are not trusted, where definitions drift, and what has to be reconciled manually before decisions can happen']
  }, {
    num: '5',
    anchor: 'capability',
    name: 'Capability Tax',
    oneLine: 'Managers cannot carry the load cleanly, so complexity routes upward.',
    feelsLike: 'Leaders spend all day translating, resolving, and stepping back into work that should not need them.',
    signals: ['leaders spend time routing work instead of leading', 'senior attention is required for normal work to move forward', 'the middle layer cannot hold decisions without escalation', 'cross-functional work stalls without constant intervention', 'execution depends on the founder or a few catchers'],
    commonMisread: 'It looks like weak management. It is often an ownership and decision-clarity problem.',
    validate: ['Where accountability breaks down under real complexity and where managers do not have the structure needed to carry the weight']
  }, {
    num: '6',
    anchor: 'technology',
    name: 'Technology Tax',
    oneLine: 'The stack exists, but it does not create real leverage.',
    feelsLike: 'You keep buying software and still end up doing the work the hard way.',
    signals: ['software does not make work meaningfully easier', 'teams route around systems because they do not fit how work actually happens', 'tools add steps instead of removing them', 'automation is brittle or avoided', 'AI produces output, but not progress'],
    commonMisread: 'It looks like the wrong tools. Often the bigger issue is unclear rules, vague ownership, and workflows that were never redesigned.',
    validate: ['Whether the business logic is clear enough for automation or AI to help at all']
  }];
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taxParam = params.get('tax')?.toLowerCase() ?? '';
    const hash = (location.hash || '').replace('#', '').toLowerCase();
    const target = taxParam || hash;
    if (!target) return;
    const idx = taxes.findIndex(t => t.anchor === target);
    if (idx === -1) return;
    setExpandedTax(idx);
    // Give React a tick to render the expanded panel before scrolling.
    setTimeout(() => {
      const el = document.getElementById(target);
      el?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 0);
  }, [location.search, location.hash]);
  return <main id="top" className="w-full" data-id="element-760">
      {/* Hero */}
      <section className="relative py-20 px-6 bg-paper pt-32 overflow-hidden" data-id="element-761">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-762">
          <BlueprintGrid data-id="element-763" />
        </div>

        <motion.div initial="hidden" animate="visible" className="max-w-4xl mx-auto relative z-10" data-id="element-764">
          <motion.h1 variants={heroHeadline} className="text-4xl md:text-6xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-765">
            If growth feels heavier than it should, the drag is usually showing
            up in one of a few places.
          </motion.h1>

          <motion.div variants={heroBody} className="text-lg text-text max-w-2xl mb-8 space-y-4" data-id="element-766">
            <p data-id="element-767">
              You feel it when decisions stall, work loops, exceptions spread,
              and the founder becomes the fallback system.
            </p>
            <p className="font-medium text-navy" data-id="element-768">
              If the right people stopped catching things, what would break?
              That is the test.
            </p>
          </motion.div>

          {/* Hero CTAs - Side by side on desktop, stacked on mobile */}
          <motion.div variants={heroCta} className="flex flex-col sm:flex-row sm:items-start gap-6 mb-10" data-id="element-769">
            {/* Primary CTA with microcopy */}
            <div className="flex flex-col" data-id="element-770">
              <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-771">
                <span data-id="element-772">Book an Honest Conversation</span>
                <ArrowRight className="w-5 h-5" data-id="element-773" />
              </Link>
              <motion.p variants={heroMicrocopy} className="text-sm text-text-muted mt-2" data-id="element-774">
                45 minutes. No pitch. Just clarity.
              </motion.p>
            </div>

            {/* Secondary link with microcopy */}
            <div className="flex flex-col" data-id="element-775">
              <a href="#six-taxes" className="inline-flex items-center space-x-2 px-6 py-4 border border-navy/20 hover:border-navy/40 text-navy/70 hover:text-navy font-medium transition-all duration-300" data-id="element-776">
                <span data-id="element-777">Jump to the six taxes</span>
                <ChevronDown className="w-4 h-4" data-id="element-778" />
              </a>
              <motion.p variants={heroMicrocopy} className="text-sm text-text-muted mt-2" data-id="element-779">
                See the full breakdown below
              </motion.p>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center" animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-780">
          <ChevronDown className="w-5 h-5 text-seaglass/50" data-id="element-781" />
        </motion.div>
      </section>

      {/* What's Actually Breaking (Warning Signs) */}
      <section id="breaking" className="py-24 px-6 border-t hairline-top bg-paper-dark" data-id="element-782">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-783">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-784">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-785">
              The warning signs
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-8" data-id="element-786">
            <p className="text-text" data-id="element-787">
              Most founders do not experience this as one dramatic failure.
            </p>

            <p className="text-text" data-id="element-788">It shows up as a pattern:</p>

            <ul className="space-y-2 ml-6" data-id="element-789">
              {['the same issue keeps returning in different forms', 'leaders spend more time routing than leading', 'work depends too much on memory and heroics', 'numbers need interpretation before anyone trusts them', 'good people are tired, but the business still feels underbuilt', 'growth is happening, but the business feels less clean and less profitable than it should'].map((item, i) => <li key={i} className="flex items-start space-x-2" data-id="element-790">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-791" />
                  <span className="text-text" data-id="element-792">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </motion.div>
      </section>

      {/* Six Taxes */}
      <section id="six-taxes" className="relative py-24 px-6 grain border-y border-seaglass/20 bg-[#8BA89F26] overflow-hidden" data-id="element-793">
        <TopographicPattern color="sage" opacity={0.06} data-id="element-794" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-795">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-796">
            <p className="eyebrow mb-4 text-seaglass" data-id="element-797">
              The Six Structural Taxes
            </p>
            <h2 className="text-5xl md:text-6xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-798">
              Which one is costing you the most?
            </h2>
            <p className="text-base text-navy font-medium mb-2" data-id="element-799">
              Each of these is a different way the load shows up once growth
              outruns the way the business actually runs.
            </p>
            <p className="text-base text-text-muted mb-2" data-id="element-800">
              You may feel more than one. That is normal.
            </p>
            <p className="text-base text-text-muted" data-id="element-801">
              The goal is not to label everything. It is to notice where the
              load is surfacing.
            </p>
          </motion.div>

          <div className="space-y-0" data-id="element-802">
            {taxes.map((tax, i) => <Fragment key={tax.anchor} data-id="element-803">
                <motion.div id={tax.anchor} variants={fadeIn} className="border-b border-seaglass/20 last:border-0 scroll-mt-28" data-id="element-804">
                  <button onClick={() => setExpandedTax(expandedTax === i ? null : i)} className="w-full py-6 flex items-start justify-between text-left group" data-id="element-805">
                    <div className="flex-1 pr-6" data-id="element-806">
                      <div className="flex items-start space-x-4 mb-2" data-id="element-807">
                        <span className="text-sm font-mono font-semibold text-seaglass uppercase tracking-wider" data-id="element-808">
                          {tax.num}
                        </span>
                        <h3 className="text-2xl font-serif font-semibold text-navy" data-id="element-809">
                          {tax.name}
                        </h3>
                      </div>
                      <p className="text-base text-text ml-10" data-id="element-810">{tax.oneLine}</p>
                      {expandedTax !== i && <div className="ml-10 mt-3 space-y-1" data-id="element-811">
                          {tax.signals.slice(0, 2).map((signal, j) => <p key={j} className="text-sm text-text-muted flex items-start" data-id="element-812">
                              <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-1.5 mr-2" data-id="element-813" />
                              <span data-id="element-814">{signal}</span>
                            </p>)}
                        </div>}
                    </div>
                    <ChevronDown className={`w-5 h-5 text-seaglass flex-shrink-0 transition-transform duration-300 ${expandedTax === i ? 'rotate-180' : ''}`} data-id="element-815" />
                  </button>

                  {expandedTax === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="pb-6 ml-10" data-id="element-816">
                      <div className="space-y-4" data-id="element-817">
                        <div data-id="element-818">
                          <p className="text-base font-semibold text-navy mb-2" data-id="element-819">
                            Common signals:
                          </p>
                          <ul className="space-y-2" data-id="element-820">
                            {tax.signals.map((signal, j) => <li key={j} className="flex items-start space-x-2 text-base text-text" data-id="element-821">
                                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-822" />
                                <span data-id="element-823">{signal}</span>
                              </li>)}
                          </ul>
                        </div>
                        <p className="text-sm text-text-muted italic pt-2 border-t border-seaglass/15" data-id="element-824">
                          Feels like: {tax.feelsLike}
                        </p>

                        <div className="pt-6 mt-6 border-t border-seaglass/15 space-y-6" data-id="element-825">
                          <div data-id="element-826">
                            <p className="text-base font-semibold text-navy mb-2" data-id="element-827">
                              Common misread
                            </p>
                            <p className="text-base text-text" data-id="element-828">
                              {tax.commonMisread}
                            </p>
                          </div>

                          <div data-id="element-829">
                            <p className="text-base font-semibold text-navy mb-2" data-id="element-830">
                              What we would validate
                            </p>
                            <ul className="space-y-2" data-id="element-831">
                              {tax.validate.map((item, k) => <li key={k} className="flex items-start space-x-2 text-base text-text" data-id="element-832">
                                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-833" />
                                  <span data-id="element-834">{item}</span>
                                </li>)}
                            </ul>
                          </div>

                          <p className="text-sm text-text-muted" data-id="element-835">
                            If this feels like your loudest tax,{' '}
                            <Link to="/honest-conversation" className="text-seaglass hover:text-seaglass-light font-medium transition-colors duration-200" data-id="element-836">
                              book an Honest Conversation →
                            </Link>
                          </p>

                          <a href="#top" className="inline-flex items-center text-sm text-seaglass hover:text-seaglass-light font-medium transition-colors duration-200" data-id="element-837">
                            Back to top →
                          </a>
                        </div>
                      </div>
                    </motion.div>}
                </motion.div>
              </Fragment>)}
          </div>

          {/* The point — payoff after reading the six taxes */}
          <motion.div variants={fadeIn} className="mt-10 pt-8 border-t border-seaglass/20" data-id="element-838">
            <div className="border-l-2 border-seaglass pl-8 py-2 mb-8" data-id="element-839">
              <p className="text-xs font-mono uppercase tracking-wider text-seaglass mb-2" data-id="element-840">
                The point
              </p>
              <p className="text-body text-text mb-3" data-id="element-841">
                You don&apos;t fix all six taxes.
              </p>
              <p className="text-body text-text" data-id="element-842">
                You identify which one is creating the most drag right now, find
                the constraint behind it, and remove the next domino.
              </p>
              <p className="text-body text-text mt-3 font-medium" data-id="element-843">
                That is how you reduce catching without adding bureaucracy.
              </p>
            </div>

            <Link to="/how-we-fix-it" className="inline-flex items-center space-x-2 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-300" data-id="element-844">
              <span data-id="element-845">See the 3-Step Sequence</span>
              <ArrowRight className="w-4 h-4" data-id="element-846" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* What Founders Usually Misread */}
      <section id="misread" className="py-24 px-6 bg-paper" data-id="element-847">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-848">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-849">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-850">
              What founders usually misread
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-8" data-id="element-851">
            <p className="text-text" data-id="element-852">
              At this stage, the most common instinct is to add more:
            </p>

            <ul className="space-y-2 ml-6" data-id="element-853">
              {['more people', 'more meetings', 'more tools', 'more approvals', 'more oversight'].map((item, i) => <li key={i} className="flex items-start space-x-2" data-id="element-854">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-855" />
                  <span className="text-text" data-id="element-856">{item}</span>
                </li>)}
            </ul>

            <div className="space-y-4" data-id="element-857">
              <p className="text-text" data-id="element-858">
                Sometimes that relieves local pressure.
              </p>
              <p className="text-text" data-id="element-859">
                But if the problem sits deeper in the way work is running,
                adding more usually adds cost, coordination, and drag.
              </p>
              <p className="text-text font-medium text-navy" data-id="element-860">
                The better move is to find the constraint first.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Example */}
      <section id="example" className="relative py-24 px-6 bg-paper-dark overflow-hidden" data-id="element-861">
        {/* Chevron accent — section ornament */}
        <div className="absolute left-1/2 -translate-x-1/2 top-[-0.5rem] opacity-[0.18] hidden lg:block" data-id="element-862">
          <ChevronAccent direction="down" size={56} color="seaglass" double data-id="element-863" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-864">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-865">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-866">
              A real example
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-8" data-id="element-867">
            <p className="text-text" data-id="element-868">
              The information exists, but the business cannot reliably surface
              what matters without someone knowing where to look and what to
              ask.
            </p>

            <p className="text-text font-medium" data-id="element-869">
              That is not a tooling problem. That is structural tax:
            </p>

            <ul className="space-y-2 ml-6" data-id="element-870">
              <li className="flex items-start space-x-2" data-id="element-871">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-872" />
                <span className="text-text" data-id="element-873">
                  <strong className="font-semibold text-navy" data-id="element-874">
                    Visibility Tax
                  </strong>{' '}
                  because reality isn't easy to see
                </span>
              </li>
              <li className="flex items-start space-x-2" data-id="element-875">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-876" />
                <span className="text-text" data-id="element-877">
                  <strong className="font-semibold text-navy" data-id="element-878">
                    Dependence Tax
                  </strong>{' '}
                  because "knowing where to look" lives in a few people
                </span>
              </li>
              <li className="flex items-start space-x-2" data-id="element-879">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-880" />
                <span className="text-text" data-id="element-881">
                  <strong className="font-semibold text-navy" data-id="element-882">
                    Opportunity Tax
                  </strong>{' '}
                  because leaders get pulled in just to locate truth
                </span>
              </li>
            </ul>

            <div className="border-l-2 border-seaglass pl-8 py-2" data-id="element-883">
              <p className="text-xs font-mono uppercase tracking-wider text-seaglass mb-2" data-id="element-884">
                The fix
              </p>
              <p className="text-body text-text font-medium" data-id="element-885">
                You don't run a business off queries. That's just catching
                things with extra steps.
              </p>
            </div>

            <p className="text-text" data-id="element-886">The fix isn't "document everything."</p>

            <p className="text-text font-medium" data-id="element-887">
              The fix is to redesign the mechanism so the recurring failure is
              hard to repeat.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What to Do Next */}
      <section id="next" className="py-24 px-6 bg-paper grain" data-id="element-888">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-889">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-890">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-891">
              What to do next
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-8" data-id="element-892">
            <p className="text-text" data-id="element-893">
              You do not need a full-company reset to move forward.
            </p>
            <p className="text-text" data-id="element-894">
              You need a clearer read on what is creating the drag so the right
              fix gets smaller, cleaner, and more useful.
            </p>

            <p className="text-text" data-id="element-895">In 45 minutes we'll:</p>

            <ul className="space-y-2 ml-6" data-id="element-896">
              {['pressure-test what you are seeing', 'clarify where the drag may actually be coming from', 'decide whether this looks structural or not', 'make the next step clearer'].map((item, i) => <li key={i} className="flex items-start space-x-2" data-id="element-897">
                  <Check className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-898" />
                  <span className="text-text" data-id="element-899">{item}</span>
                </li>)}
            </ul>

            <div className="border-l-2 border-seaglass pl-8 py-2" data-id="element-900">
              <p className="text-xs font-mono uppercase tracking-wider text-seaglass mb-2" data-id="element-901">
                Possible outcomes
              </p>
              <ul className="space-y-2 text-sm text-text" data-id="element-902">
                <li className="flex items-start space-x-2" data-id="element-903">
                  <ArrowRight className="w-3.5 h-3.5 text-seaglass flex-shrink-0 mt-1" data-id="element-904" />
                  <span data-id="element-905">You're fine. Keep pushing.</span>
                </li>
                <li className="flex items-start space-x-2" data-id="element-906">
                  <ArrowRight className="w-3.5 h-3.5 text-seaglass flex-shrink-0 mt-1" data-id="element-907" />
                  <span data-id="element-908">
                    This is real, but it's not the right timing. Wait.
                  </span>
                </li>
                <li className="flex items-start space-x-2" data-id="element-909">
                  <ArrowRight className="w-3.5 h-3.5 text-seaglass flex-shrink-0 mt-1" data-id="element-910" />
                  <span data-id="element-911">A Constraint Diagnostic is warranted.</span>
                </li>
              </ul>
            </div>

            <p className="text-text font-medium" data-id="element-912">
              If the drag isn&apos;t structural, we&apos;ll tell you.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Dark CTA Band */}
      <section className="relative py-24 px-6 bg-navy grain overflow-hidden" data-id="element-913">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-914">
          <BlueprintGrid data-id="element-915" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-916">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold text-white mb-6 leading-tight" data-id="element-917">
            Spot the constraint. Then decide what is actually worth fixing.
          </h2>
          <p className="text-lg text-white/80 mb-10" data-id="element-918">
            You do not need a full-company reset to move forward. You need a
            clearer read on what is creating the drag so the right fix gets
            smaller, cleaner, and more useful.
          </p>
          <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 group" data-id="element-919">
            <span data-id="element-920">Book an Honest Conversation</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" data-id="element-921" />
          </Link>
        </motion.div>
      </section>
    </main>;
}